import React from 'react';

import * as analytics from 'analytics/Analytics';
import {ActionOrigin, MODBIO_ANALYTICS_ACTIONS} from 'constants/AnalyticsConstants';
import {SequenceSummary} from 'constants/SequenceConstants';
import * as FileUtil from 'folder-items/FileUtil';
import {MAX_NUM_BULK_EDIT_MODS_TARGETS} from 'modbio/bulk-edit-mods/BulkEditModsConstants';
import {ListableItem} from 'search/SearchConstants';
import * as ModalContainer from 'ui-library/ModalContainer';

import NucleotidesFindAndReplaceModal from './NucleotidesFindAndReplaceModal';

function allItemsOligoOrRNASequence(items: Array<ListableItem>): boolean {
  return items.every((item) => FileUtil.isOligo(item) || FileUtil.isRnaSequence(item));
}

function allOligosSameNucleotideType(items: Array<ListableItem>): boolean {
  return new Set(items.map((item) => (item as SequenceSummary).nucleotideType)).size === 1;
}

function allItemsSameSequenceType(items: Array<ListableItem>): boolean {
  return new Set(items.map((item) => (item as SequenceSummary).sequence_type)).size === 1;
}

export function isBulkFindAndReplaceEnabled(items: Array<ListableItem>): boolean {
  return (
    allItemsOligoOrRNASequence(items) &&
    allOligosSameNucleotideType(items) &&
    allItemsSameSequenceType(items) &&
    items.length <= MAX_NUM_BULK_EDIT_MODS_TARGETS
  );
}

export function getBulkFindAndReplaceTooltipTitle(items: Array<ListableItem>): string | null {
  if (!allItemsOligoOrRNASequence(items)) {
    return 'Find-and-replace rules cannot be applied to DNA sequences';
  }

  if (!allItemsSameSequenceType(items)) {
    return 'Find-and-replace rules can only be applied to items of the same type (i.e. all RNA sequences or all oligos)';
  }

  if (!allOligosSameNucleotideType(items)) {
    return 'Find-and-replace rules can only be applied to oligos of the same nucleotide type';
  }

  if (items.length > MAX_NUM_BULK_EDIT_MODS_TARGETS) {
    return `Find-and-replace rules can only be applied to at most ${MAX_NUM_BULK_EDIT_MODS_TARGETS} items at once`;
  }

  return null;
}

export function bulkFindAndReplace(
  selectedIds: Array<string>,
  selectedSequences: Array<SequenceSummary>,
  actionOrigin: ActionOrigin
): void {
  const nucleotideTypes = new Set(selectedSequences.map((item) => item.nucleotideType));

  analytics.trackEvent(MODBIO_ANALYTICS_ACTIONS.OPENED_FIND_AND_REPLACE_MODAL, {
    actionOrigin,
  });

  ModalContainer.open(
    NucleotidesFindAndReplaceModal,
    <NucleotidesFindAndReplaceModal
      nucleotideType={Array.from(nucleotideTypes)[0]}
      sequenceIds={selectedIds}
      shouldSkipResultsScreenAfterSuccess={false}
      isBulk={true}
    />
  );
}
